/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useCallback, use, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import useAutenticacaoJwt from "@auth/services/jwt/useAutenticacaoJwt";
import { removeGlobalHeadersSpaceApi, setGlobalHeadersSpaceApi } from "@/api/space/globalHeaders.spaceApi";
import type { EmpresaAcessoItem } from "@auth/user";
import { DEFAULT_COMPANY_HEADER } from "@/configs/system";

type CompanyContextValue = {
	companyId: string | null;
	companies: EmpresaAcessoItem[];
	setCompanyId: (companyId: string) => void;
	clearCompany: () => void;
};

const CompanyContext = createContext<CompanyContextValue | null>(null);

function lsKey(userId: string) {
	return `last_company:${userId}`;
}

export function CompanyProvider({ children }: { readonly children: React.ReactNode }) {
	const queryClient = useQueryClient();
	const { user, isAuthenticated } = useAutenticacaoJwt();

	const companies = useMemo(() => user?.empresa_acesso ?? [], [user?.empresa_acesso]);
	const companyIds = useMemo(() => companies.map((company) => String(company.id_cad_empresa)), [companies]);

	const [companyIdState, setCompanyIdState] = useState<string | null>(null);

	// controla “hidratação” (aplicar última empresa) sem limpar cache
	const hydratedRef = useRef(false);
	const lastUserIdRef = useRef<string | null>(null);

	const clearCompany = useCallback(() => {
		setCompanyIdState(null);
		removeGlobalHeadersSpaceApi([DEFAULT_COMPANY_HEADER]);
		hydratedRef.current = false;
	}, []);

	type ApplyOptions = { persist?: boolean; invalidate?: boolean };
	const applyCompany = useCallback(
		(next: string | null, opts: ApplyOptions = {}) => {
			const { persist = true, invalidate = true } = opts;

			setCompanyIdState(next);

			if (!next) {
				removeGlobalHeadersSpaceApi([DEFAULT_COMPANY_HEADER]);
				return;
			}

			// header global do ky
			setGlobalHeadersSpaceApi({ [DEFAULT_COMPANY_HEADER]: next });

			if (persist && user?.id_cad_pessoa != null) localStorage.setItem(lsKey(String(user.id_cad_pessoa)), next);

			// limpa cache SOMENTE quando foi troca manual (invalidate = true)
			if (invalidate) queryClient.clear();
		},
		[queryClient, user?.id_cad_pessoa],
	);

	const setCompanyId = useCallback(
		(next: string) => {
			if (next === companyIdState) return;
			applyCompany(next, { persist: true, invalidate: true });
		},
		[applyCompany, companyIdState],
	);

	useEffect(() => {
		const userId = user?.id_cad_pessoa != null ? String(user.id_cad_pessoa) : null;

		// se mudou de usuário, reseta hidratação
		if (lastUserIdRef.current !== userId) {
			lastUserIdRef.current = userId;
			hydratedRef.current = false;
			setCompanyIdState(null);
		}

		if (!isAuthenticated || !userId) {
			clearCompany();
			return;
		}

		if (!companyIds.length) {
			applyCompany(null, { persist: false, invalidate: false });
			hydratedRef.current = true;
			return;
		}

		// evita re-hidratar toda hora
		if (hydratedRef.current) return;

		const savedRaw = localStorage.getItem(lsKey(userId));
		const saved = savedRaw ? String(savedRaw).trim() : null;

		const set = new Set(companyIds);
		const initial = (saved && set.has(saved) ? saved : companyIds[0]) ?? null;

		applyCompany(initial, { persist: true, invalidate: false });
		hydratedRef.current = true;
	}, [isAuthenticated, user, companyIds, applyCompany, clearCompany]);

	const value = useMemo(
		() => ({
			companyId: companyIdState,
			companies,
			setCompanyId,
			clearCompany,
		}),
		[companyIdState, companies, setCompanyId, clearCompany],
	);

	return <CompanyContext value={value}>{children}</CompanyContext>;
}

export function useCompany() {
	const ctx = use(CompanyContext);
	if (!ctx) throw new Error("useCompany must be used within CompanyProvider");
	return ctx;
}
